import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { auth } from "@/lib/auth/client";
import { useAuthStore } from "@/app/stores/auth";
import { LoaderCircle, LogOut, UserRound } from "lucide-react";
import { useMemo } from "react";
import { Link } from "react-router";
import { useTranslation } from "react-i18next";

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean).slice(0, 2);
  return parts.map((part) => part[0]?.toUpperCase() ?? "").join("") || "U";
}

function getAvatarUrl(user: unknown): string | null {
  if (!user || typeof user !== "object") return null;

  const candidate =
    (user as Record<string, unknown>).image ??
    (user as Record<string, unknown>).avatar ??
    (user as Record<string, unknown>).picture;

  return typeof candidate === "string" && candidate.length > 0 ? candidate : null;
}

export function AppHeader() {
  const { t } = useTranslation();
  const { data: session, isPending } = auth.useSession();

  const signOut = useAuthStore((state) => state.login.signOut);
  const initials = useMemo(() => getInitials(session?.user.name ?? "User"), [session?.user.name]);

  const avatarUrl = useMemo(() => getAvatarUrl(session?.user), [session?.user]);

  return (
    <header className="sticky top-0 z-40 border-b border-border bg-background">
      <div className="mx-auto flex h-14 w-full max-w-6xl items-center justify-between px-4 sm:px-6">
        <Link
          to="/"
          className="font-mono text-xs font-semibold uppercase tracking-[0.14em] text-primary"
        >
          tutoria
        </Link>

        {isPending ? (
          <div className="inline-flex items-center gap-2 text-sm text-muted-foreground">
            <LoaderCircle className="size-4 animate-spin" />
            {t("common.loading")}
          </div>
        ) : session ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="flex items-center gap-3 rounded-md border border-border/70 bg-card px-3 py-2 text-left outline-none transition hover:bg-accent/50 focus-visible:ring-2 focus-visible:ring-ring"
              >
                <Avatar>
                  {avatarUrl && <AvatarImage src={avatarUrl} alt={session.user.name} />}
                  <AvatarFallback className="bg-primary/15 text-xs font-semibold text-primary">
                    {initials}
                  </AvatarFallback>
                </Avatar>
                <span className="flex min-w-0 flex-col">
                  <span className="truncate text-sm font-medium leading-none">
                    {session.user.name}
                  </span>
                  <span className="truncate text-xs text-muted-foreground">
                    {session.user.email}
                  </span>
                </span>
              </button>
            </DropdownMenuTrigger>

            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="min-w-0">
                <div className="truncate text-sm font-medium">{session.user.name}</div>
                <div className="truncate text-xs text-muted-foreground">{session.user.email}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to="/dashboard">
                  <UserRound className="size-4" />
                  {t("auth.account")}
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={(event) => {
                  event.preventDefault();
                  void signOut();
                }}
                variant="destructive"
              >
                <LogOut className="size-4" />
                {t("header.signOut")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" asChild>
              <Link to="/login">{t("auth.login")}</Link>
            </Button>
            <Button size="sm" asChild>
              <Link to="/register">{t("auth.signUp")}</Link>
            </Button>
          </div>
        )}
      </div>
    </header>
  );
}
